import { type AxiosInstance } from 'axios';
import type { ApiRequestConfig } from '@/shared/api/types';
import { getApiClient } from '@/shared/api/api-client-instance';

export abstract class BaseApiService {
  protected abstract readonly resourcePath: string;

  private readonly _client: AxiosInstance;

  constructor(client?: AxiosInstance) {
    this._client = client ?? getApiClient();
  }

  protected get client(): AxiosInstance {
    return this._client;
  }

  private buildUrl(path: string): string {
    const base = path.startsWith('/') ? '' : '/';
    return `/${this.resourcePath}${base}${path}`;
  }

  protected async get<T>(path = '', config?: ApiRequestConfig): Promise<T> {
    const { data } = await this._client.get<T>(this.buildUrl(path), config);
    return data;
  }

  protected async post<T>(path = '', body?: unknown, config?: ApiRequestConfig): Promise<T> {
    const { data } = await this._client.post<T>(this.buildUrl(path), body, config);
    return data;
  }

  protected async put<T>(path = '', body?: unknown, config?: ApiRequestConfig): Promise<T> {
    const { data } = await this._client.put<T>(this.buildUrl(path), body, config);
    return data;
  }

  protected async patch<T>(path = '', body?: unknown, config?: ApiRequestConfig): Promise<T> {
    const { data } = await this._client.patch<T>(this.buildUrl(path), body, config);
    return data;
  }

  protected async delete<T>(path = '', config?: ApiRequestConfig): Promise<T> {
    const { data } = await this._client.delete<T>(this.buildUrl(path), config);
    return data;
  }

  protected async getBlob(path = ''): Promise<Blob> {
    const { data } = await this._client.get(this.buildUrl(path), { responseType: 'blob' });
    return data;
  }

  protected createCancelToken(): { token: AbortController; cancel: () => void } {
    const controller = new AbortController();
    return {
      token: controller,
      cancel: () => controller.abort(),
    };
  }
}
